import {
  BillingOperationStatus,
  InvoiceKind,
  InvoiceStatus,
} from "@api/shared-runtime/enums";
import { formatInvoiceReference } from "@api/shared-runtime/invoice-reference";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { InvoiceLinesTable } from "@/components/billing-plan/invoice-lines-table";
import {
  formatInvoicePeriod,
  formatPlanInstant,
} from "@/components/billing-plan/billing-plan-values";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  describePaidWith,
  describeRefund,
} from "@api/shared-runtime/billing-collection";
import { saveInvoiceDocument } from "@/features/organization-billing/invoice-document-download";
import { useTRPCClient } from "@/trpc";
import { DetailRow } from "./usage-billing-event-detail-sheet";
import { UsageBillingInvoiceActivity } from "./usage-billing-invoice-activity";
import { formatUsageBillingCurrency } from "./usage-billing-format";
import { formatUsageBillingDateTime } from "./usage-billing-time";
import {
  COLLECTION_PATH_LABELS,
  INVOICE_STATUS_LABELS,
  invoiceStatusVariant,
  type UnitInvoice,
} from "./usage-billing-invoice-presentation";

const OPERATION_STATUS_LABELS: Record<BillingOperationStatus, string> = {
  [BillingOperationStatus.Pending]: "In progress",
  [BillingOperationStatus.Succeeded]: "Succeeded",
  [BillingOperationStatus.Failed]: "Failed",
};

export function UsageBillingInvoiceDetailSheet({
  invoice,
  onOpenChange,
}: {
  invoice: UnitInvoice | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={Boolean(invoice)} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            Invoice
            {invoice && (
              <Badge variant={invoiceStatusVariant(invoice.status)}>
                {INVOICE_STATUS_LABELS[invoice.status]}
              </Badge>
            )}
            {invoice?.kind === InvoiceKind.Closing && (
              <Badge variant="outline">Closing</Badge>
            )}
          </SheetTitle>
          <SheetDescription className="sr-only">
            Full details for this invoice
          </SheetDescription>
        </SheetHeader>
        {invoice && <InvoiceDetailBody invoice={invoice} />}
      </SheetContent>
    </Sheet>
  );
}

function InvoiceDetailBody({ invoice }: { invoice: UnitInvoice }) {
  const usagePeriod =
    invoice.usagePeriodStart && invoice.usagePeriodEnd
      ? { start: invoice.usagePeriodStart, end: invoice.usagePeriodEnd }
      : null;
  const cyclePeriod = { start: invoice.cycleStart, end: invoice.cycleEnd };
  const usageDiffersFromCycle =
    usagePeriod !== null && usagePeriod.start !== cyclePeriod.start;
  return (
    <div className="mt-6 space-y-6">
      <dl className="space-y-4">
        {invoice.paymentServiceInvoiceNumber ? (
          <DetailRow label="Invoice number">
            {invoice.paymentServiceInvoiceNumber}
          </DetailRow>
        ) : (
          <DetailRow label="Reference">
            {formatInvoiceReference(invoice.id)}
          </DetailRow>
        )}
        {invoice.kind === InvoiceKind.Cycle && (
          <DetailRow label="Base fee period">
            {formatInvoicePeriod(cyclePeriod)}
          </DetailRow>
        )}
        <DetailRow label="Usage period">
          {usagePeriod
            ? `${formatInvoicePeriod(usagePeriod)}${usageDiffersFromCycle ? " (previous cycle)" : ""}`
            : "None — first cycle of the plan"}
        </DetailRow>
        <DetailRow label="Subtotal">
          {formatUsageBillingCurrency(invoice.subtotalAmount)}
        </DetailRow>
        <DetailRow label="Sales tax">
          {invoice.taxAmount
            ? formatUsageBillingCurrency(invoice.taxAmount)
            : "—"}
        </DetailRow>
        <DetailRow label="Total">
          {invoice.totalAmount
            ? formatUsageBillingCurrency(invoice.totalAmount)
            : "—"}
        </DetailRow>
        <DetailRow label="Amount due">
          {invoice.amountDue
            ? formatUsageBillingCurrency(invoice.amountDue)
            : "—"}
        </DetailRow>
        <DetailRow label="Issued">
          {invoice.issuedAt
            ? formatUsageBillingDateTime(invoice.issuedAt)
            : "—"}
        </DetailRow>
        <DetailRow label="Due">
          {invoice.dueAt ? formatPlanInstant(invoice.dueAt) : "—"}
        </DetailRow>
        {invoice.status === InvoiceStatus.Paid && invoice.paidAt && (
          <DetailRow label="Paid">
            {formatUsageBillingDateTime(invoice.paidAt)}
          </DetailRow>
        )}
        {describeRefund(invoice) && (
          <DetailRow label="Refunded">
            {formatUsageBillingCurrency(invoice.amountRefunded ?? "0")}
            {invoice.refundedAt
              ? ` on ${formatUsageBillingDateTime(invoice.refundedAt)}`
              : ""}
          </DetailRow>
        )}
        {invoice.status === InvoiceStatus.Void && invoice.voidedAt && (
          <DetailRow label="Voided">
            {formatUsageBillingDateTime(invoice.voidedAt)}
          </DetailRow>
        )}
        {invoice.taxTransactionError && (
          <DetailRow label="Tax ledger">
            <span className="text-destructive">
              {invoice.taxTransactionError}
            </span>
          </DetailRow>
        )}
        <DetailRow label="Collection">
          {invoice.collectionPath
            ? COLLECTION_PATH_LABELS[invoice.collectionPath]
            : "Not issued yet"}
        </DetailRow>
        {describePaidWith(invoice) && (
          <DetailRow label="Paid with">{describePaidWith(invoice)}</DetailRow>
        )}
        {invoice.events.length === 0 && invoice.chargeOperation && (
          <DetailRow label="Last charge">
            <span className="flex flex-col items-end gap-0.5">
              <span>
                {OPERATION_STATUS_LABELS[invoice.chargeOperation.status]}{" "}
                (attempt {invoice.chargeOperation.attempt})
              </span>
              {invoice.chargeOperation.error && (
                <span className="text-destructive">
                  {invoice.chargeOperation.error}
                </span>
              )}
            </span>
          </DetailRow>
        )}
      </dl>

      <UsageBillingInvoiceActivity events={invoice.events} />

      <div className="space-y-2">
        <h4 className="text-sm font-semibold">Line items</h4>
        <InvoiceLinesTable
          lines={invoice.lines}
          totalAmount={invoice.subtotalAmount}
          descriptionHeader="Description"
          formatAmount={formatUsageBillingCurrency}
        />
        <p className="text-xs text-muted-foreground">
          {invoice.usageEventCount} usage event
          {invoice.usageEventCount === 1 ? "" : "s"} linked
        </p>
      </div>

      {invoice.exportOperation?.status === BillingOperationStatus.Failed &&
        invoice.exportOperation.error && (
          <p className="text-sm text-destructive" role="alert">
            Could not email: {invoice.exportOperation.error}
          </p>
        )}
      {invoice.lastPaymentError && (
        <p className="text-sm text-destructive">
          Last payment: {invoice.lastPaymentError}
        </p>
      )}
      <div className="flex flex-wrap gap-4">
        <DownloadDocumentButton invoiceId={invoice.id} />
        {invoice.hostedInvoiceUrl && (
          <a
            href={invoice.hostedInvoiceUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-block text-xs text-muted-foreground underline"
          >
            Open in payment processor
          </a>
        )}
      </div>
    </div>
  );
}

/** The in-house document, so staff can preview a draft before issuing it. */
function DownloadDocumentButton({ invoiceId }: { invoiceId: string }) {
  const trpcClient = useTRPCClient();
  const download = useMutation({
    mutationFn: async () =>
      saveInvoiceDocument(
        await trpcClient.adminInvoices.getInvoiceDocument.query({ invoiceId }),
      ),
    onError: () => toast.error("Couldn't download the invoice"),
  });
  return (
    <button
      type="button"
      className="text-xs text-muted-foreground underline disabled:opacity-50"
      disabled={download.isPending}
      onClick={() => download.mutate()}
    >
      {download.isPending ? "Preparing PDF..." : "Download PDF"}
    </button>
  );
}
